import React from 'react';
import { PATH } from 'common/constants';
import type { NotificationOut } from 'service/notificationsService';
import { GenericNotification } from './renderers/GenericNotification';
import { CountryFallbackNotification } from './renderers/CountryFallbackNotification';

type BaseNotification = NotificationOut;

// Set when some rows loaded the default mapping because their country-specific columns were missing.
export type SurveyCountryFallback = {
  mapping_profile_id: string;
  mapping_profile_name: string;
  groups: string[];
  countries: string[];
};

export type SurveyLoadPayload = {
  survey_name?: string | null;
  country_fallback?: SurveyCountryFallback | null;
};

export type SurveyLoadSuccessNotification = BaseNotification & {
  type: 'survey.load_success';
  payload: SurveyLoadPayload;
};

export type SurveyLoadPausedNotification = BaseNotification & {
  type: 'survey.load_paused';
  payload: SurveyLoadPayload;
};

type SurveyNotification = SurveyLoadSuccessNotification | SurveyLoadPausedNotification;
type SurveyRenderers = { [N in SurveyNotification as N['type']]: (n: N) => React.ReactNode };

export const surveyRenderers: SurveyRenderers = {
  'survey.load_success': (n) => {
    const surveyName = n.payload?.survey_name ?? 'Survey';
    if (n.payload?.country_fallback) {
      return <CountryFallbackNotification surveyName={surveyName} fallback={n.payload.country_fallback} />;
    }
    return (
      <GenericNotification
        title='Survey available'
        description={`${surveyName} is now available.`}
        link={{ text: 'View survey', link: PATH.TABLE_SURVEYS }}
        tag={{ color: 'success', children: 'Available' }}
      />
    );
  },
  'survey.load_paused': (n) => (
    <GenericNotification
      title="Survey under review"
      description={`${n.payload?.survey_name ?? 'Survey'} is under review.`}
      tag={{ color: 'warning', children: 'In Review' }}
    />
  ),
};
